import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Loader2 } from "lucide-react";
import { fadeUp, LOGO_DIAMOND } from "@/lib/site";
import { trackLead, getCookie } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { ime: "", telefon: "", email: "", ordinacija: "", poruka: "" };

export default function ContactCTA() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const eventId = (window.crypto?.randomUUID?.() || `lead-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    try {
      const { data } = await axios.post(`${API}/kontakt`, {
        ...form,
        stranica: typeof window !== "undefined" ? window.location.pathname : "",
        event_id: eventId,
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        fbp: getCookie("_fbp"),
        fbc: getCookie("_fbc"),
      });
      // Meta Pixel Lead event (dedups with server CAPI via eventId)
      trackLead(eventId);
      toast.success(data.message || "Upit je poslan!");
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Došlo je do greške. Pokušajte ponovno.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative z-10 bg-[#050505] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-6 flex items-center gap-3">
              <img src={LOGO_DIAMOND} alt="" className="diamond-bullet" />
              <span className="eyebrow">Kontakt</span>
            </motion.div>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-white md:text-7xl">
              Započnimo<span className="text-[#50C878]">.</span>
            </motion.h2>
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-8 max-w-md text-base leading-relaxed text-white/55">
              Besplatne konzultacije bez obveze. Recite nam o svojoj klinici i pokazat ćemo vam
              realan plan za dolazak novih klijenata putem Meta oglasa.
            </motion.p>
            <a
              href="tel:+385955985380"
              data-testid="contact-phone"
              className="mt-10 inline-flex items-center gap-3 font-display text-2xl font-extrabold tracking-tight text-white transition-colors duration-300 hover:text-[#50C878] md:text-3xl"
            >
              <Phone size={22} className="text-[#50C878]" />
              +385 95 598 5380
            </a>
            <p className="mt-4 text-sm text-white/40">onarf.marketing@gmail.com</p>
          </div>

          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex flex-col gap-5 border border-[#222] bg-[#0a0a0a] p-6 md:p-10"
          >
            <Field label="Ime i prezime" name="ime" value={form.ime} onChange={onChange} required minLength={2} testid="field-ime" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Telefon" name="telefon" type="tel" value={form.telefon} onChange={onChange} required testid="field-telefon" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="field-email" />
            </div>
            <Field label="Naziv klinike / ordinacije" name="ordinacija" value={form.ordinacija} onChange={onChange} required testid="field-ordinacija" />
            <div className="flex flex-col gap-2">
              <label className="font-display text-xs font-bold uppercase tracking-[0.18em] text-white/60">Poruka</label>
              <textarea
                name="poruka"
                data-testid="field-poruka"
                value={form.poruka}
                onChange={onChange}
                rows={4}
                className="resize-none border border-[#222] bg-[#050505] px-4 py-3 text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#50C878]"
                placeholder="Ukratko o vašoj klinici i cilju..."
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group flex items-center justify-center gap-3 bg-[#50C878] px-8 py-5 font-display text-sm font-extrabold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-[#3EB264] disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Zakažite besplatne konzultacije <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, required, minLength, testid }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-display text-xs font-bold uppercase tracking-[0.18em] text-white/60">{label}</label>
      <input
        type={type}
        name={name}
        data-testid={testid}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        className="border border-[#222] bg-[#050505] px-4 py-3 text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#50C878]"
      />
    </div>
  );
}
